import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import { QrCodeScanner as ScanIcon, Undo as UndoIcon } from '@mui/icons-material';

const SCANNER_INTERVAL_MS = 100;
const AUTO_SUBMIT_DELAY_MS = 200;

export default function ScanPhase({ inputRef, inputValue, onChange, onSubmit, recent = [], onUndo }) {
  const { t } = useTranslation('inventory');
  const lastKeystrokeTime = useRef(null);
  const autoSubmitTimer = useRef(null);
  const isScannerInput = useRef(false);
  const currentValue = useRef('');

  useEffect(() => () => clearTimeout(autoSubmitTimer.current), []);

  const submit = (value) => {
    if (!value.trim()) return;
    clearTimeout(autoSubmitTimer.current);
    isScannerInput.current = false;
    lastKeystrokeTime.current = null;
    onSubmit(value);
  };

  const scheduleAutoSubmit = (value) => {
    clearTimeout(autoSubmitTimer.current);
    autoSubmitTimer.current = setTimeout(() => submit(value), AUTO_SUBMIT_DELAY_MS);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      submit(currentValue.current);
      return;
    }
    if (e.key.length === 1) {
      const now = Date.now();
      if (lastKeystrokeTime.current !== null && now - lastKeystrokeTime.current < SCANNER_INTERVAL_MS) {
        isScannerInput.current = true;
      }
      lastKeystrokeTime.current = now;
    }
  };

  const handleChange = (value) => {
    const prevLen = currentValue.current.length;
    currentValue.current = value;
    onChange(value);

    // Bulk input (scanner dumped entire barcode as single event)
    if (value.length - prevLen > 1) {
      scheduleAutoSubmit(value);
      return;
    }

    if (isScannerInput.current) {
      scheduleAutoSubmit(value);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, pt: 4, gap: 2 }}>
      <ScanIcon sx={{ fontSize: 64, color: 'primary.main' }} />
      <Typography variant="h5" fontWeight={600}>{t('inventory:scanner.ready')}</Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: -1 }}>
        {t('inventory:scanner.barcode_helper')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
        <TextField
          inputRef={inputRef}
          label={t('inventory:scanner.barcode_label')}
          value={inputValue}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
          autoComplete="off"
          slotProps={{ htmlInput: { enterKeyHint: 'search', 'data-testid': 'barcode-input' } }}
        />
        <Button
          variant="contained"
          disabled={!inputValue.trim()}
          onClick={() => submit(currentValue.current)}
          sx={{ minWidth: 88, borderRadius: 2 }}
        >
          {t('inventory:scanner.look_up')}
        </Button>
      </Box>

      {recent.length > 0 && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <Typography variant="overline" color="text.secondary">{t('inventory:scanner.recent')}</Typography>
          <List dense disablePadding data-testid="recent-list">
            {recent.map((entry, i) => (
              <ListItem
                key={entry.id}
                disableGutters
                divider
                secondaryAction={i === 0 && !entry.undone && onUndo && (
                  <Button size="small" startIcon={<UndoIcon />} onClick={() => onUndo(entry)} sx={{ minHeight: 44 }}>
                    {t('inventory:scanner.undo')}
                  </Button>
                )}
              >
                <ListItemText
                  sx={{ pr: i === 0 ? 12 : 0, opacity: entry.undone ? 0.5 : 1 }}
                  primary={`${entry.direction === 'use' ? '−' : '+'}${entry.qty} ${entry.uom} · ${entry.itemName}`}
                  primaryTypographyProps={{ noWrap: true, sx: { textDecoration: entry.undone ? 'line-through' : 'none' } }}
                  secondary={entry.undone ? t('inventory:scanner.undone') : entry.locationName}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}
